import AppContext from 'App/AppContext';
import { getEntity } from 'globalFunctions';
import Entity, { EntityTypes } from 'models/entity';
import { useContext, useEffect, useState } from 'react';
import { apiGetInternalAuditsForEntity } from 'services/Api/internalAuditService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { isAuditModuleEnabledForTenant } from 'utils/internalAudit';

/**
 * The audits that have this entity in their scope, as context relations of the entity: an audit is a
 * relation of everything it audits, not only of its own scene. Used by the context tab of every
 * entity family an audit can cover, which renders them in its relations list as rows of type Audit.
 *
 * Those rows carry no remove action: what an audit covers is decided in the audit itself, where the
 * status of the audit and the results already written decide whether the scope may change at all.
 *
 * Returns an empty list when the audit module is off for the tenant (the same three gates as the
 * audit navigation), so nothing changes for tenants without the module.
 */
export const useEntityAudits = (entityType: EntityTypes, entityId?: number): Entity[] => {
  const appContext = useContext(AppContext);
  const [audits, setAudits] = useState<Entity[]>([]);

  const auditModuleEnabled =
    hasUserFeature(appContext, FeatureTypes.Audit) &&
    isAuditModuleEnabledForTenant(appContext.globalDataCache.settings);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const loadData = async () => {
    if (!auditModuleEnabled || !entityId) {
      setAudits([]);

      return;
    }

    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const items = await apiGetInternalAuditsForEntity(entityType, entityId, accessToken);

      setAudits(items.map((audit) => getEntity(audit)));
    } catch (err) {
      appContext.setError(err);
    }
  };

  return audits;
};
